import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Alert, 
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { createLogger } from '../../utils/debugUtils';

const logger = createLogger('FileUpload');

/**
 * File Upload Component for Research Agent
 * Allows users to upload CSV files with website URLs
 */
function FileUpload({ 
  onFileProcessed, 
  isUploading, 
  availableColumns = [], 
  selectedColumn = '', 
  onColumnChange
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type (CSV and Excel files)
    const supportedFormats = ['.csv', '.xlsx', '.xls'];
    const isSupported = supportedFormats.some(format => 
      file.name.toLowerCase().endsWith(format)
    );
    
    if (!isSupported) {
      setError('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
      setSelectedFile(null);
      return;
    }
    
    // Clear previous errors
    setError(null);
    setSelectedFile(file);
    
    logger.info('File selected:', file.name);
  };
  
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      // Pass the file to parent component for upload handling
      onFileProcessed(selectedFile, formData);
    } catch (error) {
      logger.error('Error preparing file for upload:', error);
      setError('Error preparing file for upload');
    }
  };
  
  return (
    <Box sx={{ mb: 3 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
        <input
          accept=".csv,.xlsx,.xls"
          id="csv-file-upload"
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          disabled={isUploading}
        />
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Upload a CSV or Excel file containing website URLs to research
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <label htmlFor="csv-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFileIcon />}
                disabled={isUploading}
                size="small"
                sx={{ mr: 1 }}
              >
                Select File
              </Button>
            </label>
            
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              size="small"
            >
              {isUploading ? (
                <CircularProgress size={24} sx={{ mr: 1 }} />
              ) : null}
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </Box>
          
          {selectedFile && (
            <Typography variant="caption" color="text.secondary">
              Selected: {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
            </Typography>
          )}
        </Box>
      </Paper>
      
      {/* Column Selector - Always visible but only enabled after successful upload */}
      <FormControl fullWidth margin="normal" disabled={availableColumns.length === 0}>
        <InputLabel id="file-column-select-label">Website Column</InputLabel>
        <Select
          labelId="file-column-select-label"
          id="file-column-select"
          value={selectedColumn}
          label="Website Column"
          onChange={(e) => onColumnChange && onColumnChange(e.target.value)}
        >
          <MenuItem value="">
            <em>Select a column</em>
          </MenuItem>
          {availableColumns.map((column) => (
            <MenuItem key={column} value={column}>
              {column}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {availableColumns.length === 0 
            ? 'Upload a CSV or Excel file to see available columns' 
            : 'Select the column containing website URLs'}
        </FormHelperText>
      </FormControl>
      
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

export default FileUpload;
